import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import { useForm } from 'react-hook-form';
import MgpButton from '../../elements/MgpButton/MgpButton';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { ERROR_MESSAGES, SUPPORT_CONTACT } from '../../const/AppConst';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { IApiErrors, ISubscriptionAccessCode, ISubscriptionHandleChangeStep } from '../../context/model';
import { subscriptionConfirmEmailCode, subscriptionGetAccessToken, subscriptionSendLinkCreateAccountToEmail, subscriptionSendLinkToEmail, validEmail } from '../../services/CustomerApiService';
import { extractJwtToken, getValueNoSpace, handleKeyDown } from '../../utils/utils';
import LoaderModal from '../../modals/common/LoaderModal';
import MgpFormCheckbox from '../../elements/MgpFormCheckbox/MgpFormCheckbox';


interface SubscriptionFormEmailComponentProps {
  handleChangeStep: (step: number, data: ISubscriptionHandleChangeStep) => void;
  token: string;
  isShowPhoneNumber: boolean;
  cacheId: string
}

type SubscriptionFormEmailFormData = {
    email: string,
    confirmEmail: string,
    validationCode: string,
    phoneNumber: string,
    isForeignMobile: boolean
}

export default function SubscriptionFormEmailComponent({
  handleChangeStep,
  token,
  isShowPhoneNumber,
  cacheId
}: Readonly<SubscriptionFormEmailComponentProps>) {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [attempts, setAttempts ] = useState<number>(5);
  const [isClickable, setIsClickable] = useState<boolean>(true);
  const [timer, setTimer ] = useState<number>(60);
  const [loading, setLoading] = useState<boolean>(false);

    const onValidEmail = async (email: string, values: Yup.TestContext<any>) => {
      try {
        
        const res = await validEmail(token, email);
        if (res.data.valid !== undefined && res.data.valid === true) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        const err = error as AxiosError;
        console.log(err);
        return true;
      }
    }

  const validationSchema = Yup.object().shape({
    email: Yup.string().when([], {
     is: () => !isOpenModal,
     then: (schema) => schema.required(ERROR_MESSAGES.requiredMsg).email(ERROR_MESSAGES.invalidMsg).test("is-email-valid", ERROR_MESSAGES.invalidEmailMsg, async (value, values) => {
      if(!value) {
        return false;
      }
      const valid = await onValidEmail(value, values);
      return valid;
    }),
     otherwise: (schema) => schema.notRequired(),
    }),
    confirmEmail: Yup.string().when([], {
     is: () => !isOpenModal,
     then: (schema) => schema.required(ERROR_MESSAGES.requiredMsg).when('email', (email, schema) => {
            if(email !== ""){
                return schema.test("same-email", "La confirmation d'email saisie ne correspond pas.", (val: string) => {
                    return val && email && val === email;
            }); 
          }    
      }),
     otherwise: (schema) => schema.notRequired(),
    }),
    validationCode: Yup.string().when([], {
     is: () => isOpenModal,
     then: (schema) => schema.required("Veuillez saisir le code validation reçu par mail").matches(/^\d+$/, ERROR_MESSAGES.invalidMsg ).max(6, ERROR_MESSAGES.invalidMsg).min(6, ERROR_MESSAGES.invalidMsg),
     otherwise: (schema) => schema.notRequired(),
    }),
    phoneNumber: Yup.string()
      .when('isForeignMobile', {
          is: false,
          then: Yup.string()
                  .required(ERROR_MESSAGES.requiredMsg)
                  .matches(/^(0[6-7])([ .-]?\d{2}){4}$/, ERROR_MESSAGES.invalidFrenchMobile)
                  .min(14, ERROR_MESSAGES.invalidMsg),
      })
      .when('isForeignMobile', {
          is: true,
          then: Yup.string()
                  .required(ERROR_MESSAGES.requiredMsg)
                  .matches(/^(?:\+|00)[0-9]\d{1,14}$/, ERROR_MESSAGES.invalidForeignMobile)
                  .test("number-is-french", ERROR_MESSAGES.invalidFrenchMobileWithIndicator, async (value, values) => {
                    if(!value) {
                      return false;
                    }
                    const isFrenchNumber = value.startsWith("0033") || value.startsWith("+33");
                    return !isFrenchNumber;
                  })
                  
      }),
  })

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<SubscriptionFormEmailFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
 
    },
    resolver: yupResolver(validationSchema),
  });

  const handleGetAccessCode = async () => {
    setErrorMsg('');
    setLoading(true);
    try {
      const data = getValues();
      const extractedJwt = extractJwtToken(token);
      const propositionId = extractedJwt?.propositionId;
      const response = await subscriptionGetAccessToken(token!,propositionId!, {
        email: data?.email
      })
      setIsOpenModal(true);
      setLoading(false);
      
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      setLoading(false);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue.");
      }
    }  
  }

  const handleSubmitAccessCode = async () => {
    setLoading(true);
    try {
      const propositionId = extractJwtToken(token)?.propositionId ?? '';
      const data = {
        accessCode: getValues('validationCode')
      }
      const response = await subscriptionConfirmEmailCode(token, propositionId, data);
      if (response?.data?.status === "VALID") {
        const dataChangeStep =  {
          token: token,
          subscriptionUrl: response.data.url
        }
        handleChangeStep(2, dataChangeStep);
      } else if (response?.data?.status === "INVALID" && response?.data?.remainingRetry > 0) {
        setAttempts(response?.data?.remainingRetry);
        setErrorMsg('Le code saisi est incorrect. Veuillez réessayer.')
      } else if (response?.data?.status === "INVALID" && response?.data?.remainingRetry === 0) {
        setAttempts(response?.data?.remainingRetry);
        setErrorMsg('Vous avez atteint le nombre maximum de tentatives pour valider votre code. Veuillez cliquer sur le lien ci-dessous pour demander un nouveau code.')
      } else if (response?.data?.status === "EXPIRED") {
        setAttempts(response?.data?.remainingRetry ?? attempts -1);
        setErrorMsg("Votre code d'accès est expiré. Veuillez saisir un code valide ou demander un nouveau code.")
      } else {
        setErrorMsg("Une erreur technique est survenue.");
      }
      setLoading(false);
      
    } catch (error) {
      const err = error as AxiosError;
      setLoading(false);
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else if(err.response && err.response.status === 404) {
        setErrorMsg("Votre code d'accès est expiré. Veuillez saisir un code valide ou demander un nouveau code.");
      }else {
        setErrorMsg("Une erreur technique est survenue.");
      }
    }  
  }

  const handleClick = async () => {
    setIsClickable(false);
    setAttempts(5);

    handleGetAccessCode();

    let interval = setInterval(() => {
      setTimer((prevTime) => prevTime - 1);
    }, 1000);

    setTimeout(() => {
      setIsClickable(true);
      setTimer(60);
      clearInterval(interval)
    }, 60000);
  }

  const handleSentLink = async (data: SubscriptionFormEmailFormData) => {
     setLoading(true);
    try {
      const propositionId = extractJwtToken(token)?.propositionId ?? '';
      if (isShowPhoneNumber) {
        const dataBody = {
          email: data.email,
          phoneNumber: getValueNoSpace(data.phoneNumber)
        }
        const response = await subscriptionSendLinkCreateAccountToEmail(token, cacheId, propositionId, dataBody);
        setLoading(false);
        handleChangeStep(2, {});
      } else {
        const dataBody = {
          email: data.email,
        }
        const response =  await subscriptionSendLinkToEmail(token, propositionId, dataBody);
        setLoading(false);
        const dataChangeStep = {
          token: token,
          subscriptionUrl: response.data.url
        }

        handleChangeStep(2, dataChangeStep);
      }
      
    } catch (error) {
      const err = error as AxiosError;
      setLoading(false);
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue.");
      }
    }  
  }
  
  const isForeignMobileValue = watch("isForeignMobile");

  const handleChangeisForeignMobileCheckbox = (e: any) => {
    setValue("isForeignMobile", e.target.checked);
    clearErrors("phoneNumber");

    if (e.target.checked) {
      setValue("phoneNumber", "+");
    } else {
      setValue("phoneNumber", "");
    }
  }

  const handleChangePhoneNumber = (e: any) => {
    const regexSpace = new RegExp("(.{"+ 2 + "})", "g");
    let target = e.currentTarget, position = target.selectionEnd, length = target.value.length;
    target.value = target.value.replace(/\s/g, '').replace(regexSpace, '$1 ').trim();
    target.selectionEnd = position += ((target.value.charAt(position - 1) === ' ' && target.value.charAt(length - 1) === ' ' && length !== target.value.length) ? 1 : 0);
    setValue("phoneNumber", target.value);
  }


    return (             
        <form style={{width: '100%'}}>

          <p className='card-title bleu mb-sm'>{isOpenModal ? 'Renseignez le code de validation reçu par email' : isShowPhoneNumber ? 'Renseignez vos données personnelles': 'Renseignez votre email personnel'}</p>
          { isShowPhoneNumber && <p className='mb-lg'>Vous êtes affilié au contrat collectif de votre employeur. Pour gérer votre contrat et accéder à nos services, nous vous invitions à créer votre espace adhérent. Pour cela, nous vous remercions d'indiquer votre adresse email et votre numéro de téléphone portable.</p>}

          {errorMsg && <p className='bg-fushia blanc p-x-lg p-y-md m-y-md'>{errorMsg}</p>}
          {
            isOpenModal ? 
            <>
              <div className="form-row">          
                <div className="form-group mw-100">
                    <MgpFormInput
                        label="Votre code de validation"
                        id="validationCode"
                        labelFontWeight='regular'
                        register={register} 
                        errors={errors}
                        required={true}
                    />
                </div>  
              </div>
              {isClickable ? <p className='p-y-md'>Vous n'avez pas reçu le code ? <a className="link" onClick={handleClick} role='button' tabIndex={0} onKeyDown={(e) => handleKeyDown(e)}>Cliquez ici pour reçevoir un nouveau code</a></p> : <p className='p-y-md'><span>Vous pouvez demander un nouveau code dans {timer} secondes</span></p>}  
              <a className="bleu link hide-in-desktop" href={`tel:${SUPPORT_CONTACT}`}>Besoin d'aide ?</a>

              <div className='form-row jc-right mt-xl'>
                <MgpButton
                    id="submitButton"
                    type="button"
                    text= {"Suivant"}
                    icon='chevron-right'
                    iconPosition="right"
                    onClick={handleSubmit(handleSubmitAccessCode)}
                    disabled={attempts < 1}
                ></MgpButton>
              </div>
            </>
            :
            <>
              {/* <p className='text mt-xs mb-lg'>Vous allez recevoir un code de validation à saisir pour finaliser votre identification.</p> */}
              <div className='form-row jc-center'>
                <div className="form-group mw-100">
                  <MgpFormInput
                      label="Votre email"
                      id="email"
                      labelFontWeight='medium'
                      disabled={false}
                      required
                      register={register}
                      errors={errors}
                  />
                </div>
              </div>

              <div className='form-row jc-center'>
                <div className="form-group mw-100">
                  <MgpFormInput
                      label="Confirmation de votre email"
                      id="confirmEmail"
                      labelFontWeight='medium'
                      disabled={false}
                      required
                      register={register}
                      errors={errors}
                      onPaste={(e:any) => e.preventDefault()}
                      onCopy={(e:any) => e.preventDefault()}
                  />
                </div>
              </div>

              {
                isShowPhoneNumber &&
                <div className='form-row'>
                  <div className="form-group mw-100">
                    <p className='text fw-medium'>Numéro de téléphone portable <span className='fushia'>(*)</span></p>
                    <div className='form-group form-check mw-100'>
                      <MgpFormCheckbox 
                        id='isForeignMobile' 
                        label="Numéro hors France métropolitaine" 
                        register={register} 
                        errors={errors} 
                        required={false}
                        inputSize='small'
                        onChange={(e: any) => handleChangeisForeignMobileCheckbox(e)}
                      />
                    </div>
                    <div className='mt-sm'>
                      {
                        watch("isForeignMobile") ?
                        <MgpFormInput
                          label=""
                          id="phoneNumber"
                          placeholder="Ex: 00xxxxxxxx ou +xxxxxxxxxx"
                          labelFontWeight='medium'
                          maxLength={15}
                          register={register} 
                          errors={errors}
                          required={true}
                          value={watch("phoneNumber")}
                        />
                        :
                        <MgpFormInput
                          label=""
                          id="phoneNumber"
                          placeholder="Ex: 06 05 04 03 02"
                          labelFontWeight='medium'
                          maxLength={14}
                          autospace={2}
                          register={register} 
                          errors={errors}
                          required={true}
                          value={watch("phoneNumber")}
                          onChange={(e) => handleChangePhoneNumber(e)}
                        />
                      }
                      
    
                    </div>
                  </div> 
                </div>
              }

              {
                isForeignMobileValue &&
                  <div className="form-group mw-100 mt-sm">
                    <p className='bg-menthe blanc p-xs flex va-center jc-start' style={{gap: "10px", flexWrap: "unset"}}>
                      <i className='icon-info blanc '></i>
                      <span >Veuillez saisir votre numéro de mobile complet avec l'indicatif.</span>
                    </p>
                  </div>
              } 

              <a className="bleu link hide-in-desktop" href={`tel:${SUPPORT_CONTACT}`}>Besoin d'aide ?</a>

              <div className='form-row jc-right mt-xl'>
                <MgpButton
                    id="submitButton"
                    type="button"
                    text= {"Suivant"}
                    icon='chevron-right'
                    iconPosition="right"
                    onClick={handleSubmit(handleSentLink)}
                ></MgpButton>
              </div>
            </> 
          }

          { loading && <LoaderModal hasMessage={true}/> }
            
      </form>
  )
}
